import { PrimaryButton } from "@/components/buttons/PrimaryButton";
import { PencomRoutes } from "@/router/PencomRoutes";
import { useNavigate, useLocation } from "react-router-dom";
import { useForm } from "@tanstack/react-form";
import { toast } from "@/utils/toast";
import type { RCLookupResponse } from "@/models/responses/employer-registration.response";
import {
  useResendEmployerOTP,
  useGetOrganizationTypes,
  useValidateTin,
} from "@/hooks/employer-registration/employer-registration.hook";
import type { TinValidationUiState } from "@/components/onboarding/EmployerAdditionalFieldsSection";
import { useState, useEffect, useMemo, useRef } from "react";
import {
  getFieldsForOrganizationType,
  getOrganizationNumberLabel,
  getOrganizationNumberPrefix,
} from "@/utils/organizationTypeFields";
import { EmployerRegStepIndicator } from "@/components/onboarding/EmployerRegStepIndicator";
import { EmployerAdditionalFieldsSection } from "@/components/onboarding/EmployerAdditionalFieldsSection";
import { EmployerDocumentsSection } from "@/components/onboarding/EmployerDocumentsSection";
import { EmployerDetailsIdentityFieldsSection } from "@/components/onboarding/EmployerDetailsIdentityFieldsSection";
import { EmployerDetailsContactFieldsSection } from "@/components/onboarding/EmployerDetailsContactFieldsSection";
import { validateAndBuildEmployerDetailsSubmission } from "@/utils/employerDetailsSubmission";
import { useAnalytics } from "@/hooks/useAnalytics";

// Auto-validate while typing once the TIN looks substantial enough to be
// worth a FIRS call, so the employer isn't forced to click away from the
// field to see a result. Debounced so we don't fire on every keystroke.
const TIN_AUTO_VALIDATE_MIN_LENGTH = 4;
const TIN_DEBOUNCE_MS = 600;

export const EmployerDetailsPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const resendOTPMutation = useResendEmployerOTP();
  const validateTinMutation = useValidateTin();
  const { trackEvent, eventNames } = useAnalytics();
  const { data: organizationTypesData } = useGetOrganizationTypes();

  const lookupData = (location.state as { lookupData?: RCLookupResponse })
    ?.lookupData;

  // TIN validation state, co-located with the TIN field (which lives in
  // `additionalFields.tinNumber`, not the TanStack form).
  const [tinValidation, setTinValidation] = useState<TinValidationUiState>({
    status: "idle",
  });
  const [lastCheckedTin, setLastCheckedTin] = useState<string | null>(null);
  const tinDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [additionalFields, setAdditionalFields] = useState<
    Record<string, string>
  >({});
  const [documents, setDocuments] = useState<Record<string, File | null>>({});
  const [additionalFieldErrors, setAdditionalFieldErrors] = useState<
    Record<string, string>
  >({});
  const [documentErrors, setDocumentErrors] = useState<string[]>([]);

  const selectedOrgType = useMemo(() => {
    if (!lookupData?.organizationTypeCode || !organizationTypesData)
      return null;
    return organizationTypesData.find(
      (type) => type.code === lookupData.organizationTypeCode,
    );
  }, [lookupData?.organizationTypeCode, organizationTypesData]);

  const dynamicFieldConfigs = useMemo(() => {
    if (!lookupData?.organizationTypeCode) return { fields: [], documents: [] };
    const configs = getFieldsForOrganizationType(
      lookupData.organizationTypeCode,
      selectedOrgType?.requiredFields,
      selectedOrgType?.requiredDocuments,
    );

    if (lookupData.organizationTypeCode === "PR") {
      return {
        ...configs,
        fields: configs.fields.filter(
          (field) => field.name !== "companyRegistrationNumber",
        ),
      };
    }

    const isRCSector = lookupData?.sectorCode?.toUpperCase() === "RC";
    if (!isRCSector) {
      return configs;
    }

    return {
      ...configs,
      fields: configs.fields.filter(
        (field) => field.name !== "companyRegistrationNumber",
      ),
    };
  }, [
    lookupData?.organizationTypeCode,
    lookupData?.sectorCode,
    selectedOrgType,
  ]);

  const organizationNumberPrefix = getOrganizationNumberPrefix(
    lookupData?.organizationTypeCode || "",
  );
  const organizationNumberLabel = getOrganizationNumberLabel(
    lookupData?.organizationTypeCode || "",
  );

  const form = useForm({
    defaultValues: {
      companyName: lookupData?.organizationName || "",
      organizationNumber: "",
      organizationTypeCode: lookupData?.organizationTypeCode || "",
      sectorCode: lookupData?.sectorCode || "",
      emailAddress: "",
      employerAddress: "",
      phoneNumber: "",
      directorName: "",
      directorEmail: "",
    },
    onSubmit: async ({ value }) => {
      const isRCSector = value.sectorCode?.toUpperCase() === "RC";

      const result = validateAndBuildEmployerDetailsSubmission({
        value,
        organizationNumberPrefix,
        isRCSector,
        dynamicFieldConfigs,
        additionalFields,
        documents,
      });

      if (!result.valid) {
        if (result.kind === "additional_fields") {
          setAdditionalFieldErrors(result.additionalFieldErrors);
        }
        if (result.kind === "documents") {
          setDocumentErrors(result.documentErrors);
        }
        toast.error(result.toastMessage);
        return;
      }

      setAdditionalFieldErrors({});
      setDocumentErrors([]);

      resendOTPMutation.mutate(
        {
          companyName: value.companyName,
          email: value.emailAddress.trim(),
        },
        {
          onSuccess: () => {
            toast.success("OTP sent to your email. Please check your inbox.");
            trackEvent(eventNames.onboardingStepSubmitted, {
              step: "employer_details",
              status: "success",
            });
            navigate(PencomRoutes.EMPLOYER_REG_EMAIL_VERIFICATION, {
              state: {
                contactEmail: value.emailAddress.trim(),
                formData: result.formData,
              },
            });
          },
          onError: (error) => {
            toast.error(
              error.message || "Failed to send OTP. Please try again.",
            );
            trackEvent(eventNames.onboardingStepSubmitted, {
              step: "employer_details",
              status: "error",
              message: error.message,
            });
          },
        },
      );
    },
  });

  const runTinValidation = (taxId: string) => {
    const orgNumber = String(
      form.getFieldValue("organizationNumber") || "",
    ).trim();
    // The RC candidate is the full (prefixed) organization number the user
    // entered — the same value persisted as companyRegistrationNumber.
    const rcNumber = orgNumber
      ? `${organizationNumberPrefix}${orgNumber}`
      : undefined;

    setLastCheckedTin(taxId);
    setTinValidation({ status: "validating" });

    validateTinMutation.mutate(
      { taxId, orgName: lookupData?.organizationName, rcNumber },
      {
        onSuccess: (data) => {
          setTinValidation({ status: "result", data });
          trackEvent(eventNames.tinValidationResult, {
            status: data.result.status,
            rcMatches: data.crossCheck.rcMatches,
          });
        },
        onError: () => {
          // Network/timeout/429/500 surface as thrown errors here. Treat them
          // as the non-blocking FIRS-outage state so the user can continue.
          setTinValidation({
            status: "result",
            data: {
              result: { status: "ERROR" },
              crossCheck: { nameMatches: false, rcMatches: false },
            },
          });
          trackEvent(eventNames.tinValidationResult, {
            status: "ERROR",
            rcMatches: false,
          });
        },
      },
    );
  };

  const maybeValidateTin = (rawTin: string) => {
    const taxId = rawTin.trim();
    // Not every employer's TIN is 13 characters, so we don't gate on length —
    // validate any non-empty value, as long as it changed since the last
    // check. The 13-char cap is still enforced by the field itself.
    if (!taxId) return;
    if (taxId === lastCheckedTin) return;
    runTinValidation(taxId);
  };

  const handleTinBlur = (rawTin: string) => {
    // Leaving the field means "I'm done" — validate immediately regardless
    // of length, and cancel any pending debounced check for the same value.
    if (tinDebounceRef.current) clearTimeout(tinDebounceRef.current);
    maybeValidateTin(rawTin);
  };

  const handleTinChange = () => {
    // A fresh edit invalidates any prior result and re-arms the trigger.
    if (tinValidation.status !== "idle") setTinValidation({ status: "idle" });
    if (lastCheckedTin !== null) setLastCheckedTin(null);
  };

  // Debounced auto-validate while typing, so the employer doesn't have to
  // click out of the field to see a result — only once the TIN reaches a
  // reasonable minimum length, to avoid firing on the first digit or two.
  useEffect(() => {
    const taxId = (additionalFields.tinNumber || "").trim();
    if (tinDebounceRef.current) clearTimeout(tinDebounceRef.current);
    if (taxId.length < TIN_AUTO_VALIDATE_MIN_LENGTH) return;

    tinDebounceRef.current = setTimeout(() => {
      maybeValidateTin(taxId);
    }, TIN_DEBOUNCE_MS);

    return () => {
      if (tinDebounceRef.current) clearTimeout(tinDebounceRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [additionalFields.tinNumber]);

  const tinValidating = tinValidation.status === "validating";
  // Blocks on NOT_ACTIVATED, and on a VALID TIN whose company name and/or RC
  // number don't match what the employer entered — both are required for a
  // clean pass (see EmployerAdditionalFieldsSection). NOT_FOUND and INVALID
  // do not block: the employer can still submit, and PenCom re-checks the
  // TIN during admin review.
  const tinBlocksSubmit =
    tinValidation.status === "result" &&
    (tinValidation.data.result.status === "NOT_ACTIVATED" ||
      (tinValidation.data.result.status === "VALID" &&
        (!tinValidation.data.crossCheck.rcMatches ||
          !tinValidation.data.crossCheck.nameMatches)));

  useEffect(() => {
    if (!lookupData) {
      toast.error("Please complete the lookup process first");
      navigate(PencomRoutes.EMPLOYER_CODE_REG);
    }
  }, [lookupData, navigate]);

  useEffect(() => {
    trackEvent(eventNames.onboardingPageView, {
      step: "employer_details",
    });
  }, []);

  if (!lookupData) return null;

  return (
    <div className="w-full flex flex-col px-8 lg:px-[100px] relative">
      <div className="w-full flex flex-col max-w-[635px] mx-auto mt-10">
        <EmployerRegStepIndicator />
        <div className="flex items-center flex-col gap-y-4 mb-10">
          <h2 className="text-2xl font-semibold font-clash-display text-medium-dark-grey text-center">
            {lookupData?.organizationName}
          </h2>
          <p className="text-medium-dark-grey text-sm font-regular font-clash-display text-center tracking-[-0.07px]">
            Complete employer details below
          </p>
        </div>

        <div className="flex flex-col gap-y-6">
          <EmployerDetailsIdentityFieldsSection
            form={form}
            lookupData={lookupData}
            selectedOrgType={selectedOrgType}
            organizationNumberLabel={organizationNumberLabel}
            organizationNumberPrefix={organizationNumberPrefix}
          />

          <EmployerAdditionalFieldsSection
            fields={dynamicFieldConfigs.fields}
            additionalFields={additionalFields}
            setAdditionalFields={setAdditionalFields}
            additionalFieldErrors={additionalFieldErrors}
            setAdditionalFieldErrors={setAdditionalFieldErrors}
            tinValidation={tinValidation}
            onTinBlur={handleTinBlur}
            onTinChange={handleTinChange}
          />

          <EmployerDetailsContactFieldsSection form={form} />

          <EmployerDocumentsSection
            fields={dynamicFieldConfigs.documents}
            documents={documents}
            setDocuments={setDocuments}
            documentErrors={documentErrors}
            setDocumentErrors={setDocumentErrors}
          />
        </div>

        <form.Subscribe
          selector={(state) => [state.canSubmit, state.isSubmitting]}
          children={([canSubmit, isSubmitting]) => {
            const requiredAdditionalFieldsComplete = dynamicFieldConfigs.fields
              .filter((f) => f.required)
              .every(
                (f) =>
                  additionalFields[f.name]?.trim() &&
                  additionalFields[f.name].trim().length > 0,
              );
            const requiredDocsComplete = dynamicFieldConfigs.documents
              .filter((d) => d.required)
              .every((d) => documents[d.name]);
            const allRequiredComplete =
              requiredAdditionalFieldsComplete && requiredDocsComplete;

            return (
              <PrimaryButton
                className={`mt-10 ${
                  !canSubmit ||
                  !allRequiredComplete ||
                  resendOTPMutation.isPending ||
                  tinValidating ||
                  tinBlocksSubmit
                    ? "opacity-40"
                    : ""
                }`}
                title={
                  resendOTPMutation.isPending
                    ? "Processing..."
                    : isSubmitting
                      ? "Processing..."
                      : tinValidating
                        ? "Validating TIN..."
                        : "Confirm & Continue"
                }
                onClick={form.handleSubmit}
                disabled={
                  !canSubmit ||
                  !allRequiredComplete ||
                  isSubmitting ||
                  resendOTPMutation.isPending ||
                  tinValidating ||
                  tinBlocksSubmit
                }
                isLoading={resendOTPMutation.isPending}
              />
            );
          }}
        />
      </div>
    </div>
  );
};
